/**
 * ProbenchAnalytics.js — Generic PostHog tracking for Probench
 * Version: 1.0
 *
 * Drop this script into any theme layout AFTER PostHog is initialized.
 * It auto-tracks page views, AJAX calls, clicks, form submissions,
 * dialogs, and custom events — WITHOUT modifying individual feature files.
 *
 * Usage:
 *   1. Include this script after the PostHog init snippet in theme layouts.
 *   2. Optionally add data-track attributes to HTML elements for named events.
 *   3. That's it. Everything else is automatic.
 *
 * Data attribute API:
 *   data-track="event_name"                — fires on click (or submit for forms)
 *   data-track-properties='{"key":"val"}'  — optional JSON properties
 *   data-track-on="change"                 — override trigger event (default: click)
 *   data-track-label="My Button"           — override element label
 */
(function (window, $) {
    'use strict';

    // Guard: PostHog must be loaded
    if (typeof window.posthog === 'undefined') {
        return;
    }

    var PB = window.ProbenchAnalytics = {};
    var posthog = window.posthog;

    // ─────────────────────────────────────────────
    // 1. CONFIGURATION
    // ─────────────────────────────────────────────

    PB.config = {
        // Enable/disable individual auto-tracking features
        trackElementClicks: true,
        trackAjax: true,
        trackClicks: true,
        trackForms: true,
        trackDialogs: true,
        trackPageView: true,
        trackErrors: true,
        trackTimeOnPage: true,

        // AJAX URLs to ignore (partial match, case-insensitive)
        ajaxIgnore: [
            '/posthog',
            '/static/array.js',
            'google',
            'fonts.googleapis',
            'cdn.datatables',
            'cdnjs.cloudflare',
            'jquery-ui',
            'code.jquery.com'
        ],

        // Sanitize sensitive fields from tracked data
        sensitiveFields: ['password', 'token', 'secret', 'creditcard', 'ssn', 'cvv'],

        // ── AJAX URL → Feature Name mapping ──
        // Maps API URL patterns to friendly feature names.
        // In PostHog, filter by "feature_name" to answer:
        //   "How many users use AI Assistant?" → feature_name = "AI Assistant"
        //   "How many users use Bookmarks?"    → feature_name = "Bookmarks"
        ajaxFeatureMap: [
            // AI Assistant
            { pattern: '/Survey/SummarizeText',       feature: 'AI Assistant',       action: 'Summarize' },
            { pattern: '/Survey/GetGenAIResponses',    feature: 'AI Assistant',       action: 'Ask AI' },
            { pattern: '/Survey/GeneratePrompt',       feature: 'AI Assistant',       action: 'Generate Prompt' },

            // Citations / Evidence
            { pattern: '/evidence/citations',          feature: 'Citations',          action: 'Get Citations' },

            // Bookmarks
            { pattern: '/Bookmark/SaveNode',           feature: 'Bookmarks',          action: 'Save Bookmark' },
            { pattern: '/Bookmark/DeleteNode',         feature: 'Bookmarks',          action: 'Delete Bookmark' },
            { pattern: '/Bookmark/fetchBookmark',      feature: 'Bookmarks',          action: 'Fetch Bookmarks' },

            // Response History
            { pattern: 'getresponsehistory',           feature: 'Response History',   action: 'View History' },
            { pattern: 'getalluserdata',               feature: 'Response History',   action: 'View All Users' },
            { pattern: 'getlastyeardata',              feature: 'Response History',   action: 'View Previous Years' },

            // Share Pages
            { pattern: 'updateshareuserdetails',       feature: 'Share Pages',        action: 'Update Share User' },
            { pattern: 'deleteshareuserdetails',       feature: 'Share Pages',        action: 'Delete Share User' },
            { pattern: 'updatesharepagedetails',       feature: 'Share Pages',        action: 'Update Share Page' },

            // Registration
            { pattern: '/Registration/Register',       feature: 'Registration',       action: 'Register' },
            { pattern: '/Registration/Associate',      feature: 'Registration',       action: 'Associate' },
            { pattern: '/Registration/Save',           feature: 'Registration',       action: 'Save Fields' },
            { pattern: '/Registration/Companies',      feature: 'Registration',       action: 'Fetch Companies' },

            // User Management
            { pattern: '/UserManagement/AddNewUser',   feature: 'User Management',    action: 'Add User' },
            { pattern: '/UserManagement/UpdateUserStatus', feature: 'User Management', action: 'Update Status' },
            { pattern: '/get/UserManagement',          feature: 'User Management',    action: 'View Users' },

            // Notifications
            { pattern: '/UserManagement/Notifications', feature: 'Notifications',     action: 'View Notifications' },
            { pattern: '/UserManagement/RemoveNotifications', feature: 'Notifications', action: 'Remove Notification' },

            // Survey Core
            { pattern: '/Survey/GetAllIndicatorStatuses', feature: 'Survey',          action: 'Load Indicators' },
            { pattern: '/Survey/RequiredPagesLink',    feature: 'Survey',             action: 'Required Pages' },
            { pattern: '/Survey/MandatoryQuestionCount', feature: 'Survey',           action: 'Mandatory Count' },
            { pattern: '/Survey/GetCompletionQuestion', feature: 'Survey',            action: 'Completion Progress' },
            { pattern: '/Survey/GetPageIdsHavingUnreviewedComments', feature: 'Survey', action: 'Unreviewed Comments' },

            // Validation
            { pattern: '/Validation/UpdateValidationStatus', feature: 'Validation',   action: 'Update Status' },
            { pattern: '/Validation/GetInvalidQuestions', feature: 'Validation',       action: 'Get Errors' },

            // Import/Export
            { pattern: '/ImportSurveyDefinitions',     feature: 'Import/Export',      action: 'Import Definitions' },

            // Brochure / Scorecard
            { pattern: '/Brochure/ScorecardLink',      feature: 'Scorecard',          action: 'Get Link' },

            // Logging
            { pattern: '/Log/AutoSaveFailed',          feature: 'Auto Save',          action: 'Save Failed' },
            { pattern: '/Log/ManagerHistory',          feature: 'Manager Logs',       action: 'Log Action' },

            // Operations handler (legacy endpoint)
            { pattern: '/operations/',                 feature: 'Operations',         action: 'General' }
        ]
    };


    // ─────────────────────────────────────────────
    // 2. UTILITY HELPERS
    // ─────────────────────────────────────────────

    /**
     * Convert a URL path to a clean event name.
     * e.g., "/api/survey/save" → "api.survey.save"
     *       "/Manager/Handlers/ParticipantHandler.ashx" → "manager.handlers.participanthandler"
     */
    function urlToEventName(url) {
        if (!url) return 'unknown';
        // Remove protocol and host if present
        var path = url.replace(/^https?:\/\/[^\/]+/, '');
        // Remove query string and hash
        path = path.split('?')[0].split('#')[0];
        // Remove file extensions
        path = path.replace(/\.(ashx|aspx|asmx|html|htm|json|xml|js|css)$/i, '');
        // Convert to dot notation
        path = path.replace(/^\/+|\/+$/g, '').replace(/\//g, '.');
        return path.toLowerCase();
    }

    /**
     * Match an AJAX URL to a friendly feature name using ajaxFeatureMap.
     * Returns { feature: 'AI Assistant', action: 'Summarize' } or null.
     */
    function matchAjaxFeature(url) {
        if (!url) return null;
        var fullUrl = url.toLowerCase();
        var map = PB.config.ajaxFeatureMap;
        for (var i = 0; i < map.length; i++) {
            if (fullUrl.indexOf(map[i].pattern.toLowerCase()) !== -1) {
                return { feature: map[i].feature, action: map[i].action };
            }
        }
        return null;
    }

    /**
     * Safely parse JSON string, return null on failure.
     */
    function safeParseJSON(str) {
        try { return JSON.parse(str); } catch (e) { return null; }
    }

    /**
     * Extract a readable label from a DOM element.
     */
    function getElementLabel(el) {
        var $el = $(el);
        return $el.attr('data-track-label')
            || $el.attr('title')
            || $el.attr('aria-label')
            || $.trim($el.text()).substring(0, 80)
            || $el.attr('id')
            || '';
    }


    // ─────────────────────────────────────────────
    // 3. AUTO-CAPTURE CLICKS ON ELEMENTS WITH ID
    // ─────────────────────────────────────────────
    // Any clickable element (button, a, input, select, etc.) that has an
    // HTML id attribute is automatically tracked. Zero manual setup needed.
    //
    // Event name:  element.clicked
    // Properties:  element_id, element_tag, element_type, element_text,
    //              parent_id, page_url, pb_feature

    if (PB.config.trackElementClicks && $) {
        // Clickable element selectors
        var clickableSelector = 'a[id], button[id], input[id], select[id], textarea[id], '
            + '[role="button"][id], [onclick][id], .btn[id], [data-toggle][id], '
            + 'label[id], li[id], span[id], div[id]';

        // Throttle map to avoid duplicate rapid-fire events on same element
        var lastClickTime = {};
        var THROTTLE_MS = 500;

        $(document).on('click', clickableSelector, function (e) {
            var el = this;
            var id = el.id;
            if (!id) return;

            // Skip if already handled by data-track
            if ($(el).attr('data-track')) return;

            // Throttle — ignore if same element clicked within 500ms
            var now = Date.now();
            if (lastClickTime[id] && (now - lastClickTime[id]) < THROTTLE_MS) return;
            lastClickTime[id] = now;

            var $el = $(el);
            var tag = el.tagName.toLowerCase();
            var type = ($el.attr('type') || '').toLowerCase();
            var text = getElementLabel(el);

            // Build a readable event name from the ID
            // e.g., "btnSaveSurvey" → "btn_save_survey"
            //        "enei-contrast-toggle" → "enei_contrast_toggle"
            var cleanId = id
                .replace(/([a-z])([A-Z])/g, '$1_$2')  // camelCase → camel_Case
                .replace(/[-\s]+/g, '_')                // dashes/spaces → underscore
                .toLowerCase();

            posthog.capture('element.clicked', {
                element_id: id,
                element_id_clean: cleanId,
                element_tag: tag,
                element_type: type || undefined,
                element_text: text,
                element_classes: ($el.attr('class') || '').substring(0, 200),
                parent_id: ($el.parent().attr('id') || $el.closest('[id]').attr('id') || undefined),
                page_url: window.location.pathname,
                page_title: document.title
            });
        });

        // Also track change events on form elements with IDs
        $(document).on('change', 'select[id], input[type="checkbox"][id], input[type="radio"][id]', function () {
            var el = this;
            var id = el.id;
            if (!id || $(el).attr('data-track')) return;

            var $el = $(el);
            var tag = el.tagName.toLowerCase();
            var type = ($el.attr('type') || '').toLowerCase();

            // Get value safely (don't capture sensitive inputs)
            var value = undefined;
            var isSensitive = false;
            for (var s = 0; s < PB.config.sensitiveFields.length; s++) {
                if (id.toLowerCase().indexOf(PB.config.sensitiveFields[s]) !== -1) {
                    isSensitive = true;
                    break;
                }
            }

            if (!isSensitive) {
                if (tag === 'select') {
                    value = $el.find('option:selected').text().substring(0, 100);
                } else if (type === 'checkbox') {
                    value = el.checked ? 'checked' : 'unchecked';
                } else if (type === 'radio') {
                    value = $el.val();
                }
            }

            posthog.capture('element.changed', {
                element_id: id,
                element_tag: tag,
                element_type: type || undefined,
                element_value: value,
                page_url: window.location.pathname
            });
        });
    }


    // ─────────────────────────────────────────────
    // 4. AJAX INTERCEPTION — Auto-track all $.ajax
    // ─────────────────────────────────────────────

    if (PB.config.trackAjax && $) {
        var originalAjax = $.ajax;

        $.ajax = function (urlOrSettings, settings) {
            // Normalize arguments — $.ajax(url, settings) or $.ajax(settings)
            var opts;
            if (typeof urlOrSettings === 'string') {
                opts = $.extend({}, settings || {}, { url: urlOrSettings });
            } else {
                opts = $.extend({}, urlOrSettings);
            }

            var url = opts.url || '';
            var method = (opts.type || opts.method || 'GET').toUpperCase();

            // Check ignore list
            var shouldIgnore = false;
            for (var i = 0; i < PB.config.ajaxIgnore.length; i++) {
                if (url.toLowerCase().indexOf(PB.config.ajaxIgnore[i].toLowerCase()) !== -1) {
                    shouldIgnore = true;
                    break;
                }
            }

            if (!shouldIgnore) {
                var eventName = 'ajax.' + urlToEventName(url);
                var startTime = Date.now();
                var featureMatch = matchAjaxFeature(url);

                var trackProps = {
                    ajax_url: url.split('?')[0],
                    ajax_method: method,
                    page_url: window.location.pathname,
                    feature_name: featureMatch ? featureMatch.feature : 'Other',
                    feature_action: featureMatch ? featureMatch.action : urlToEventName(url)
                };

                // Wrap success callback
                var origSuccess = opts.success;
                opts.success = function () {
                    var duration = Date.now() - startTime;
                    posthog.capture(eventName + '.success', $.extend({}, trackProps, {
                        ajax_duration_ms: duration
                    }));
                    if (origSuccess) origSuccess.apply(this, arguments);
                };

                // Wrap error callback
                var origError = opts.error;
                opts.error = function (xhr, status, error) {
                    var duration = Date.now() - startTime;
                    posthog.capture(eventName + '.error', $.extend({}, trackProps, {
                        ajax_duration_ms: duration,
                        ajax_status: xhr ? xhr.status : 0,
                        ajax_error: (error || status || '').toString().substring(0, 200)
                    }));
                    if (origError) origError.apply(this, arguments);
                };
            }

            // Call original $.ajax
            return originalAjax.call($, opts);
        };

        // Preserve $.ajax properties (e.g., $.ajax.active)
        for (var ajaxKey in originalAjax) {
            if (originalAjax.hasOwnProperty(ajaxKey)) {
                $.ajax[ajaxKey] = originalAjax[ajaxKey];
            }
        }
    }


    // ─────────────────────────────────────────────
    // 4. DECLARATIVE TRACKING — data-track attributes
    // ─────────────────────────────────────────────

    if (PB.config.trackClicks && $) {
        $(document).on('click change submit', '[data-track]', function (e) {
            var $el = $(this);
            var trackOn = $el.attr('data-track-on') || (this.tagName === 'FORM' ? 'submit' : 'click');

            // Only fire for the correct event type
            if (e.type !== trackOn) return;

            var eventName = $el.attr('data-track');
            var propsStr = $el.attr('data-track-properties');
            var props = propsStr ? safeParseJSON(propsStr) : {};
            props = props || {};

            // Auto-add element context
            props.element_text = getElementLabel(this);
            props.element_tag = this.tagName.toLowerCase();
            if (this.id) props.element_id = this.id;
            props.page_url = window.location.pathname;

            posthog.capture(eventName, props);
        });
    }


    // ─────────────────────────────────────────────
    // 5. FORM SUBMISSION TRACKING
    // ─────────────────────────────────────────────

    if (PB.config.trackForms && $) {
        $(document).on('submit', 'form', function () {
            // Skip if already tracked via data-track
            if ($(this).attr('data-track')) return;

            var formId = this.id || this.name || 'unknown_form';
            var formAction = (this.action || '').split('?')[0];

            posthog.capture('form.submitted', {
                form_id: formId,
                form_action: urlToEventName(formAction),
                form_method: (this.method || 'GET').toUpperCase(),
                page_url: window.location.pathname
            });
        });
    }


    // ─────────────────────────────────────────────
    // 6. DIALOG / MODAL TRACKING
    // ─────────────────────────────────────────────

    if (PB.config.trackDialogs && $) {
        // jQuery UI Dialog — intercept .dialog('open') and initial open
        if ($.fn.dialog) {
            var origDialog = $.fn.dialog;
            $.fn.dialog = function (action) {
                var result = origDialog.apply(this, arguments);

                if (action === 'open' || (typeof action === 'object')) {
                    var dialogTitle = this.attr('title')
                        || (typeof action === 'object' && action.title)
                        || this.attr('id')
                        || 'unknown_dialog';

                    posthog.capture('dialog.opened', {
                        dialog_title: String(dialogTitle).substring(0, 100),
                        dialog_id: this.attr('id') || undefined,
                        page_url: window.location.pathname
                    });
                }

                return result;
            };

            // Preserve prototype chain
            for (var dkey in origDialog) {
                if (origDialog.hasOwnProperty(dkey)) {
                    $.fn.dialog[dkey] = origDialog[dkey];
                }
            }
        }

        // Bootstrap Modal — track shown.bs.modal
        $(document).on('shown.bs.modal', '.modal', function () {
            var modalTitle = $(this).find('.modal-title').text()
                || $(this).attr('id')
                || 'unknown_modal';

            posthog.capture('modal.opened', {
                modal_title: $.trim(modalTitle).substring(0, 100),
                modal_id: this.id || undefined,
                page_url: window.location.pathname
            });
        });
    }


    // ─────────────────────────────────────────────
    // 7. AUTO PAGE VIEW WITH FEATURE DETECTION
    // ─────────────────────────────────────────────

    if (PB.config.trackPageView) {
        var masterFeatureMap = {
            // Frontend — Non-Survey pages
            '/signin/': { area: 'frontend', feature: 'Sign In' },
            '/home/': { area: 'frontend', feature: 'Home' },
            '/registration/': { area: 'frontend', feature: 'Registration' },
            '/choosecompany/': { area: 'frontend', feature: 'Choose Company' },
            '/forgotpassword/': { area: 'frontend', feature: 'Forgot Password' },
            '/newpassword/': { area: 'frontend', feature: 'Change Password' },
            '/associate/': { area: 'frontend', feature: 'Associate' },
            '/sharehistory/': { area: 'frontend', feature: 'Share History' },
            '/confirmregistration/': { area: 'frontend', feature: 'Confirm Registration' },
            '/usermanagement/': { area: 'frontend', feature: 'User Management' },
            '/progressdoughnut/': { area: 'frontend', feature: 'Progress Doughnut' },
            '/error/': { area: 'frontend', feature: 'Error Page' },
            '/feedback/': { area: 'frontend', feature: 'Feedback' },

            // Frontend — Survey pages
            '/survey/': { area: 'frontend', feature: 'Survey' },
            '/responsehistory/': { area: 'frontend', feature: 'Response History' },
            '/actionplan/': { area: 'frontend', feature: 'Action Plan' },
            '/evidencelocker/': { area: 'frontend', feature: 'Evidence Locker' },
            '/summary/': { area: 'frontend', feature: 'Summary' },
            '/printsurvey/': { area: 'frontend', feature: 'Print Survey' },
            '/calculator/': { area: 'frontend', feature: 'Calculator' },
            '/surveypdf/': { area: 'frontend', feature: 'Survey PDF' },

            // Frontend — Dashboard pages
            '/dashboard/': { area: 'frontend', feature: 'Dashboard' },
            '/sharabledashboard/': { area: 'frontend', feature: 'Sharable Dashboard' },
            '/authscorecard/': { area: 'frontend', feature: 'Scorecard' },
            '/documentdownload/': { area: 'frontend', feature: 'Document Download' },

            // Manager pages
            '/manager/': { area: 'manager', feature: 'Manager' }
        };

        var path = window.location.pathname.toLowerCase();
        var matched = { area: 'unknown', feature: 'Unknown' };

        // Exact match for home page
        if (path === '/') {
            matched = { area: 'frontend', feature: 'Home' };
        } else {
            for (var mapKey in masterFeatureMap) {
                if (path.indexOf(mapKey) !== -1) {
                    matched = masterFeatureMap[mapKey];
                    break;
                }
            }
        }

        // Register as super properties (attached to ALL future events automatically)
        if (typeof posthog.register === 'function') {
            posthog.register({
                pb_area: matched.area,
                pb_feature: matched.feature,
                pb_page_path: path,
                pb_page_title: document.title
            });
        }

        // Fire a named page view event
        posthog.capture('page.viewed', {
            area: matched.area,
            feature: matched.feature,
            page_path: path,
            page_title: document.title,
            referrer: document.referrer || undefined
        });
    }


    // ─────────────────────────────────────────────
    // 8. JAVASCRIPT ERROR TRACKING
    // ─────────────────────────────────────────────

    if (PB.config.trackErrors) {
        window.addEventListener('error', function (e) {
            posthog.capture('js.error', {
                error_message: (e.message || '').substring(0, 500),
                error_source: e.filename || undefined,
                error_line: e.lineno || undefined,
                error_col: e.colno || undefined,
                page_url: window.location.pathname
            });
        });
    }


    // ─────────────────────────────────────────────
    // 9. MANUAL CAPTURE HELPER
    // ─────────────────────────────────────────────

    /**
     * ProbenchAnalytics.track('feature.action', { key: 'value' })
     *
     * Convenience wrapper that adds standard context automatically.
     * Use this for the few cases where you need explicit tracking
     * that AJAX interception or data-track attributes can't cover.
     */
    PB.track = function (eventName, properties) {
        if (typeof posthog === 'undefined') return;
        var props = $.extend({
            page_url: window.location.pathname,
            page_title: document.title
        }, properties || {});
        posthog.capture(eventName, props);
    };


    // ─────────────────────────────────────────────
    // 10. SESSION TIMING — Track time on page
    // ─────────────────────────────────────────────

    if (PB.config.trackTimeOnPage) {
        var pageLoadTime = Date.now();

        window.addEventListener('beforeunload', function () {
            var timeOnPage = Math.round((Date.now() - pageLoadTime) / 1000);
            posthog.capture('page.left', {
                time_on_page_seconds: timeOnPage,
                page_url: window.location.pathname
            });
        });
    }

})(window, window.jQuery || window.$);